interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number; // milisaniye cinsinden
}

/**
 * Uygulama içi önbellek.
 *
 * Tasarım:
 *  - Birincil katman **bellek** (`Map`). Okuma/yazmada `JSON.parse`/`stringify`
 *    maliyeti yoktur, ana thread bloke olmaz, kota sınırı yoktur.
 *  - `localStorage` yalnızca **best-effort** ikinci katmandır: sayfa yenilemeleri
 *    arasında küçük verileri korumak için. Yazımlar try/catch ile sarılıdır; belirli
 *    bir boyutun (MAX_PERSIST_BYTES) üstündeki değerler localStorage'a hiç yazılmaz,
 *    böylece 22 MB'lık GeoJSON / büyük olay listeleri / raporlar depoyu şişirmez.
 *  - `QuotaExceededError` durumunda süresi dolmuş ve en eski girdiler tahliye edilip
 *    tek sefer yeniden denenir; yine olmazsa yazımdan sessizce vazgeçilir (bellek yeter).
 *
 * Bu sayede "localStorage dolunca yüklemeler bozuluyor / cache temizleyince
 * hızlanıyor" sorunları ortadan kalkar.
 */
class CacheService {
  private static instance: CacheService;
  private DEFAULT_EXPIRY = 5 * 60 * 1000; // 5 dakika varsayılan
  private AUTH_TOKEN_KEY = 'auth_token';
  private AUTH_TOKEN_EXPIRY = 24 * 60 * 60 * 1000; // 24 saat

  // localStorage'a yazılacak tek bir değer için üst sınır (~256 KB serialize).
  // Üstündekiler yalnızca bellekte tutulur.
  private MAX_PERSIST_BYTES = 256 * 1024;

  // Birincil, hızlı önbellek.
  private memory = new Map<string, CacheItem<any>>();

  private constructor() {}

  public static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  private isExpired(item: CacheItem<any>, now: number = Date.now()): boolean {
    return now - item.timestamp > item.expiry;
  }

  // Veriyi önbelleğe al
  public setItem<T>(key: string, data: T, expiry: number = this.DEFAULT_EXPIRY): void {
    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiry
    };
    this.memory.set(key, cacheItem);
    this.persist(key, cacheItem);
  }

  // localStorage'a güvenli (best-effort) yazma
  private persist<T>(key: string, cacheItem: CacheItem<T>): void {
    let serialized: string;
    try {
      serialized = JSON.stringify(cacheItem);
    } catch {
      return; // Serialize edilemiyorsa sadece bellekte kalsın
    }

    // Büyük değerleri localStorage'a hiç yazma (yaklaşık 2 byte/char).
    if (serialized.length * 2 > this.MAX_PERSIST_BYTES) {
      // Depoda bu anahtarın eski/küçük bir sürümü varsa temizle ki tutarsızlık olmasın.
      try { localStorage.removeItem(key); } catch { /* yoksay */ }
      return;
    }

    try {
      localStorage.setItem(key, serialized);
    } catch {
      // Büyük ihtimalle QuotaExceededError: yer açıp bir kez daha dene.
      this.evictForSpace();
      try {
        localStorage.setItem(key, serialized);
      } catch {
        // Yine olmadı; bellek önbelleği yeterli, sessizce geç.
      }
    }
  }

  // Süresi dolmuş ve en eski önbellek girdilerini temizleyerek localStorage'da yer aç.
  // Yalnızca bizim CacheItem formatındaki anahtarlara dokunur (authToken, userId vb. korunur).
  private evictForSpace(): void {
    const now = Date.now();
    const live: { key: string; timestamp: number }[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const raw = localStorage.getItem(k);
      if (!raw) continue;

      let parsed: any;
      try {
        parsed = JSON.parse(raw);
      } catch {
        continue; // Bizim formatımızda değil (ör. JWT string) — dokunma
      }

      if (parsed && typeof parsed.timestamp === 'number' && typeof parsed.expiry === 'number') {
        if (now - parsed.timestamp > parsed.expiry) {
          localStorage.removeItem(k); // süresi dolmuş
        } else {
          live.push({ key: k, timestamp: parsed.timestamp });
        }
      }
      // .timestamp/.expiry taşımayan (report_eventsdata_, report_saved_ vb.) anahtarlara dokunma
    }

    // Hâlâ yer gerekiyorsa en eski %25 cache girdisini at.
    live.sort((a, b) => a.timestamp - b.timestamp);
    const dropCount = Math.ceil(live.length / 4);
    for (let i = 0; i < dropCount; i++) {
      localStorage.removeItem(live[i].key);
    }
  }

  // Veriyi önbellekten al
  public getItem<T>(key: string): T | null {
    const now = Date.now();

    // 1) Bellek katmanı (hızlı yol)
    const mem = this.memory.get(key);
    if (mem) {
      if (this.isExpired(mem, now)) {
        this.clear(key);
        return null;
      }
      return mem.data as T;
    }

    // 2) localStorage katmanı (yenileme sonrası soğuk okuma)
    let item: string | null = null;
    try {
      item = localStorage.getItem(key);
    } catch {
      return null;
    }
    if (!item) return null;

    try {
      const cacheItem = JSON.parse(item) as CacheItem<T>;

      // Önbellek süresi dolmuş mu kontrol et
      if (this.isExpired(cacheItem, now)) {
        localStorage.removeItem(key); // Süresi dolmuş veriyi temizle
        return null;
      }

      this.memory.set(key, cacheItem); // Sonraki okumalar bellekten gelsin
      return cacheItem.data;
    } catch (error) {
      console.error("Önbellek verisi ayrıştırılamadı:", error);
      try { localStorage.removeItem(key); } catch { /* yoksay */ }
      return null;
    }
  }

  // Önbellekten veriyi al, yoksa fetch fonksiyonunu çalıştır
  public async getOrFetch<T>(
    key: string,
    fetchFn: () => Promise<T>,
    expiry: number = this.DEFAULT_EXPIRY
  ): Promise<T> {
    // Önbellekte var mı kontrol et
    const cachedData = this.getItem<T>(key);
    if (cachedData) {
      return cachedData;
    }

    // Yoksa API'den çek
    try {
      const data = await fetchFn();
      this.setItem<T>(key, data, expiry);
      return data;
    } catch (error) {
      console.error(`Veri çekilirken hata oluştu (${key}):`, error);
      throw error;
    }
  }

  // Belirli önbellek girdisini temizle
  public clear(key: string): void {
    this.memory.delete(key);
    try { localStorage.removeItem(key); } catch { /* yoksay */ }
  }

  // Belirli bir önekle başlayan tüm önbellek girdilerini temizle
  public clearByPrefix(prefix: string): void {
    for (const k of Array.from(this.memory.keys())) {
      if (k.startsWith(prefix)) this.memory.delete(k);
    }
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith(prefix))
        .forEach(key => localStorage.removeItem(key));
    } catch { /* yoksay */ }
  }

  // Tüm önbelleği temizle
  public clearAll(): void {
    this.memory.clear();
    try { localStorage.clear(); } catch { /* yoksay */ }
  }

  // Önbelleği yenile - veriyi yeniden getir
  public async refresh<T>(
    key: string,
    fetchFn: () => Promise<T>,
    expiry: number = this.DEFAULT_EXPIRY
  ): Promise<T> {
    this.clear(key);
    return this.getOrFetch<T>(key, fetchFn, expiry);
  }

  // Auth token için özel metodlar
  public setAuthToken(token: string): void {
    this.setItem(this.AUTH_TOKEN_KEY, token, this.AUTH_TOKEN_EXPIRY);
  }

  public getAuthToken(): string | null {
    return this.getItem(this.AUTH_TOKEN_KEY);
  }

  public clearAuthToken(): void {
    this.clear(this.AUTH_TOKEN_KEY);
  }
}

export default CacheService.getInstance();
